import {
  ActionIcon,
  Anchor,
  Code,
  Group,
  Modal,
  NumberFormatter,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconExternalLink, IconPencil } from '@tabler/icons-react';
import type { Id, PaymentPlanAttributes } from 'api/models';
import { useDebtor } from 'app/Private/Clients/Detail/client-atom';
import { PaymentPlanDetail } from 'app/Private/PaymentPlans';
import { InfoRow } from 'components/InfoRow';
import { Panel } from 'components/Panel';
import { tKeys, useTranslation } from 'locales';
import { useHistory } from 'react-router';
import { isNonNullish } from 'remeda';
import { getCurrencySymbolFromCurrencyCode } from 'shared/utils/currency';
import { formatBankAccount } from 'shared/utils/payment';
import { useGetConstants } from 'shared/utils/selectors';
import { languageName } from 'types';
import { ClientStatusBadges } from './ClientStatusBadges';
import { PopoverInfo } from './PopoverInfo';

type UglyPaymentPlan = PaymentPlanAttributes & { id: Id };

export const DebtorInfoPanel = ({ withBorder = false }: { withBorder?: boolean }) => {
  const { t } = useTranslation();
  const history = useHistory();

  const debtor = useDebtor();

  const [isPaymentPlanModalOpened, { open: openModal, close: closePaymentPlanModal }] =
    useDisclosure(false);

  const handleClientSettings = () => {
    history.push(`/clients/${debtor.id}/edit`);
  };

  const goToPlan = () => {
    if (debtor.payment_plans_attributes?.length > 1) {
      history.push(`/payment-plans?debtor_id=${debtor.id}&status=in_progress`);
      return;
    }
    openModal();
  };

  return (
    <>
      <Panel
        w="100%"
        h="100%"
        title={t(tKeys.CLIENT.GENERAL_INFO_TAB.PANEL_TITLE.INFORMATION)}
        withBorder={withBorder}
        topRight={
          <ActionIcon onClick={handleClientSettings} aria-label={t(tKeys.EDIT)} variant="default">
            <IconPencil size={18} />
          </ActionIcon>
        }
        topLeft={<ClientStatusBadges onPaymentPlanClick={goToPlan} />}
      >
        <Stack gap="lg" flex={1}>
          <FirstColumnInfo goToPlan={goToPlan} />
          <SecondColumnInfo />
        </Stack>
      </Panel>
      {debtor.has_payment_plan && (
        <Modal
          size="80%"
          title={t(tKeys.PAYMENT_PLAN.TITLE.ONE)}
          opened={isPaymentPlanModalOpened}
          onClose={closePaymentPlanModal}
        >
          <PaymentPlanDetail
            planId={(debtor.payment_plans_attributes[0] as UglyPaymentPlan).id}
            inModal
          />
        </Modal>
      )}
    </>
  );
};

// This is temporary, in order to have the old UI and the new UI living side by side
// This will be refactored when feature flag is removed
const FirstColumnInfo = ({ goToPlan }: { goToPlan: () => void }) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const debtor = useDebtor();

  const {
    additional_line_1: additionalLine1,
    additional_line_2: additionalLine2,
    street_name: streetName,
    street_number: streetNumber,
    street_box: streetBox,
    zip_code: zipCode,
    country: countryName,
    city,
  } = debtor?.address_attributes || {};

  return (
    <>
      {isNonNullish(debtor.external_id) && (
        <InfoRow label={t(tKeys.EXTERNAL_ID)}>
          <Code>{debtor.external_id}</Code>
        </InfoRow>
      )}
      {isNonNullish(debtor.csv_id) && (
        <InfoRow label={t(tKeys.CSV_ID)}>
          <Code>{debtor.csv_id}</Code>
        </InfoRow>
      )}
      <InfoRow label={t(tKeys.ADDRESS)}>
        {streetName || zipCode || city || countryName ? (
          <Stack gap={0}>
            <Text ta="right" c="gray.10" fw={500}>
              {streetName ?? ''}
              {streetNumber && `, ${streetNumber}`}
              {streetBox && ` ${t(tKeys.FORM.ADDRESS.BOX)} ${streetBox}`}
            </Text>
            {additionalLine1 && (
              <Text ta="right" c="gray.10" fw={500}>
                {additionalLine1}
              </Text>
            )}
            {additionalLine2 && (
              <Text ta="right" c="gray.10" fw={500}>
                {additionalLine2}
              </Text>
            )}
            <Text ta="right" c="gray.10" fw={500}>
              {zipCode ?? ''}
              {city && ` ${city}`}
              {countryName && (city || zipCode) && `, ${countryName}`}
            </Text>
            {countryName && !city && !zipCode && (
              <Text ta="right" c="gray.10" fw={500}>
                {countryName}
              </Text>
            )}
          </Stack>
        ) : (
          'N/A'
        )}
      </InfoRow>
      <InfoRow label={t(tKeys.EMAIL)}>
        {debtor.emails?.[0] ? (
          <Group wrap="nowrap" maw="80%" gap={4}>
            <Tooltip label={debtor.emails[0]} withArrow openDelay={500}>
              <Text truncate c="gray.10" fw={500}>
                {debtor.emails[0]}
              </Text>
            </Tooltip>
            <PopoverInfo items={debtor.emails} />
          </Group>
        ) : (
          'N/A'
        )}
      </InfoRow>
      <InfoRow label={t(tKeys.PHONE)}>
        {debtor.phones_attributes?.length > 0 ? (
          <Group gap={4}>
            <Text c="gray.10" fw={500}>
              {debtor.phones_attributes[0].number}
            </Text>
            <PopoverInfo items={debtor.phones_attributes.map(phone => phone.number)} />
          </Group>
        ) : (
          <Text c="gray.10">N/A</Text>
        )}
      </InfoRow>
      <InfoRow label={t(tKeys.ACCOUNT_MANAGER)}>
        <Text c="gray.10" fw={500}>
          {debtor.account_manager
            ? `${debtor.account_manager.first_name} ${debtor.account_manager.last_name}`
            : 'N/A'}
        </Text>
      </InfoRow>
      {debtor.minimum_saldo_sending !== 0 && (
        <InfoRow label={t(tKeys.MIN_SALDO_SENDING)}>
          <Text c="gray.10" fw={500}>
            <NumberFormatter
              value={debtor.minimum_saldo_sending}
              thousandSeparator=" "
              suffix={`${getCurrencySymbolFromCurrencyCode(debtor.currency)}`}
            />
          </Text>
        </InfoRow>
      )}
      {debtor.recovery_plan && (
        <InfoRow label={t(tKeys.RECOVERY_PLAN)}>
          <Tooltip label={debtor.recovery_plan.name} withArrow openDelay={500}>
            <Text c="gray.10" fw={500}>
              {debtor.recovery_plan.name}
            </Text>
          </Tooltip>
        </InfoRow>
      )}
      {debtor.days_between_reminder >= 1 && (
        <InfoRow label={t(tKeys.DELAY_BETWEEN_REMINDERS)}>
          <Text c="gray.10" fw={500}>
            {t(tKeys.DAYS, { count: debtor.days_between_reminder })}
          </Text>
        </InfoRow>
      )}
      {/* I'd rather have an anchor if only one and adapt popover for multiple PP */}
      {debtor.has_payment_plan && (
        <InfoRow label={t(tKeys.PAYMENT_PLAN.TITLE.ONE)}>
          <Anchor onClick={goToPlan} c="purple.5" fw={500}>
            <Group gap={4}>
              {debtor.payment_plans_attributes[0]?.workflow?.attributes?.name ??
                t(tKeys.DEBTOR.VIEW_PAYMENT_PLAN)}
              <IconExternalLink color={theme.colors.purple[5]} size={20} stroke={1.5} />
            </Group>
          </Anchor>
        </InfoRow>
      )}
    </>
  );
};

const SecondColumnInfo = () => {
  const { t } = useTranslation();
  const constants = useGetConstants();
  const debtor = useDebtor();

  return (
    <>
      <InfoRow label={t(tKeys.VAT_NUMBER)}>
        <Text c="gray.10" fw={500}>
          {debtor.vat_number ?? 'N/A'}
        </Text>
      </InfoRow>
      <InfoRow label={t(tKeys.COMMON.BANK_ACCOUNT)}>
        {debtor.debtor_bank_accounts_attributes?.[0]?.iban ? (
          <Stack gap={0}>
            <Group gap={4} align="start">
              <Text c="gray.10" fw={500}>
                {formatBankAccount(debtor.debtor_bank_accounts_attributes[0]?.iban)}
              </Text>
              <PopoverInfo
                items={debtor.debtor_bank_accounts_attributes.flatMap(account =>
                  [account.iban, account.bic].filter(Boolean),
                )}
              />
            </Group>
            {debtor.debtor_bank_accounts_attributes[0]?.bic && (
              <Text ta="end" c="gray.10" fw={500}>
                {`BIC : ${debtor.debtor_bank_accounts_attributes[0]?.bic}`}
              </Text>
            )}
          </Stack>
        ) : (
          <Text c="gray.10" fw={500}>
            N/A
          </Text>
        )}
      </InfoRow>
      {debtor.locale != null && (
        <InfoRow label={t(tKeys.FORM.CLIENT.CONTACT_LANG)}>
          <Text c="gray.10" fw={500}>
            {languageName(debtor.locale.toLowerCase())}
          </Text>
        </InfoRow>
      )}
      {debtor.co_contractor && (
        <InfoRow label={t(tKeys.CLIENT.DETAIL.COCONTRACTOR)}>
          <Text c="gray.10" fw={500}>
            {t(tKeys.CLIENT.DETAIL.APPLIED_VAT_0)}
          </Text>
        </InfoRow>
      )}
      {debtor.intracom && (
        <InfoRow label={t(tKeys.INVOICE.INTRACOMMUNITY)}>
          <Text c="gray.10" fw={500}>
            {t(tKeys.CLIENT.DETAIL.APPLIED_VAT_0)}
          </Text>
        </InfoRow>
      )}
      {debtor.currency && (
        <InfoRow label={t(tKeys.DEBTOR.CURRENCY)}>
          <Text c="gray.10" fw={500}>
            {`${debtor.currency} (${getCurrencySymbolFromCurrencyCode(debtor.currency)})`}
          </Text>
        </InfoRow>
      )}
      {debtor.reference && (
        <InfoRow label={t(tKeys.DEBTOR.REFERENCE)}>
          <Text c="gray.10" fw={500}>
            {debtor.reference}
          </Text>
        </InfoRow>
      )}
      {debtor.default_invoice_template && (
        <InfoRow label={t(tKeys.INVOICE.TEMPLATE)}>
          <Text c="gray.10" fw={500}>
            {debtor.default_invoice_template.name}
          </Text>
        </InfoRow>
      )}
      {debtor.default_credit_note_template && (
        <InfoRow label={t(tKeys.INVOICING.CREDIT_NOTE_TEMPLATE)}>
          <Tooltip openDelay={500} label={debtor.default_credit_note_template.name} withArrow>
            <Text c="gray.10" fw={500}>
              {debtor.default_credit_note_template.name}
            </Text>
          </Tooltip>
        </InfoRow>
      )}
      {debtor.sending_media_attributes?.length > 0 && (
        <InfoRow label={t(tKeys.DEBTOR.SENDING_MEDIUMS)}>
          <Text c="gray.10" fw={500}>
            {debtor.sending_media_attributes.map(
              (sendingMedium, index) =>
                `${index !== 0 ? ' - ' : ''}${
                  constants.sending_medium.find(item => sendingMedium.name === item.value)!
                    .description
                }`,
            )}
          </Text>
        </InfoRow>
      )}
    </>
  );
};
